import {
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import * as React from 'react';
import { Box, Typography } from '@mui/material';
import { Payload } from 'recharts/types/component/DefaultTooltipContent';
import { lightBlue } from '@mui/material/colors';
import Theme, { grey } from 'theme';

function DonutDot(props: {
  cx?: number;
  cy?: number;
  stroke?: string;
  fill?: string;
}) {
  const { cx, cy, stroke, fill } = props;
  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill='none'
        stroke={stroke || fill}
        strokeWidth={2}
      />
      <circle
        cx={cx}
        cy={cy}
        r={2}
        fill='#fff'
        stroke='none'
      />
    </g>
  );
}

type CustomTooltipProps = {
  tickFormatter?: (value: number) => string;
  label?: number;
  payload?: Payload<string | number | (string | number)[], string | number>[] | undefined;
  active?: boolean;
};

function CustomTooltip({
  active,
  payload,
  label,
  tickFormatter,
}: CustomTooltipProps) {
  if (active && payload && payload.length) {
    return (
      <Box sx={{ minWidth: 100, background: lightBlue[50], py: 0.5, px: 1.5 }}>
        <Typography variant='caption' color='text.primary'>
          {label}
        </Typography>
        {tickFormatter ? (
          <Typography variant='caption' color='text.primary' component='p'>
            {tickFormatter(payload[0].value as number)}
          </Typography>
        ) : payload[0].value}
      </Box>
    );
  }

  return null;
}

interface AreaChartComponentProps {
  data: { [key: string]: string | number }[];
  dataKeyX: string;
  dataKeyLine: string;
  v2?: boolean;
  strokeColor?: string;
  tickFormatter?: (value: number) => string;
}

export default function SingleAreaChart({
  data,
  dataKeyX,
  dataKeyLine,
  v2,
  strokeColor,
  tickFormatter,
}: AreaChartComponentProps): JSX.Element {
  const uniqueDates = [] as { [key: string]: string | number }[];
  data.reduce((acc, curr) => {
    if (acc[curr[dataKeyX] as number] === undefined) {
      uniqueDates.push(curr);
    }
    return {
      ...acc,
      [curr[dataKeyX] as string]: curr.value,
    };
  }, {} as { [key: string]: string | number });

  const tickFormatterFn = (value: number) => {
    if (tickFormatter) {
      return [1, 4, 7].includes(value) ? tickFormatter(value) : '';
    }
    return `${value}`;
  };

  const gradientId = `areaGradient-${strokeColor?.replace('#', '')}`;

  return (
    <ResponsiveContainer width='100%' maxHeight={300} height='100%'>
      <AreaChart data={uniqueDates} margin={{ left: v2 ? 55 : 0, top: v2 ? 10 : 5, bottom: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
            <stop offset='5%' stopColor={strokeColor} stopOpacity={0.8} />
            <stop offset='65%' stopColor={strokeColor} stopOpacity={0.1} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeWidth={1} stroke={grey[50]} />
        <XAxis
          dataKey={dataKeyX}
          fontSize={12}
          height={20}
          stroke={grey[50]}
          tick={{ fill: Theme.palette.text.secondary }}
        />
        <YAxis
          width={20}
          axisLine={false}
          domain={v2 ? [1, 7] : [0, 10]}
          interval={0}
          fontSize={12}
          tickMargin={v2 ? 10 : undefined}
          ticks={
            v2
              ? Array.from({ length: 7 }, (_, i) => i + 1)
              : Array.from({ length: 6 }, (_, i) => i * 2)
          }
          tickFormatter={tickFormatterFn}
          stroke={grey[50]}
          tick={{ fill: Theme.palette.text.secondary }}
        />
        <Tooltip
          content={<CustomTooltip tickFormatter={tickFormatter} />}
          wrapperStyle={{ color: 'black' }}
        />
        <Area
          type='monotone'
          dataKey={dataKeyLine}
          stroke={strokeColor}
          fill={`url(#${gradientId})`}
          strokeWidth={1}
          dot={DonutDot}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
